import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { bff } from '@/lib/http/bff';

type ArtifactPlot = {
  x_ms?: Array<number | null>;
  series?: Record<string, Array<number | boolean | null>>;
};

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function toNumeric(v: number | boolean | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
}

/** Smart number formatter: picks precision based on magnitude. */
function fmtNum(v: number, forceDecimals?: number): string {
  if (!Number.isFinite(v)) return '—';
  if (forceDecimals != null) return v.toFixed(forceDecimals);
  const abs = Math.abs(v);
  if (abs === 0) return '0';
  if (abs >= 10_000) return v.toFixed(0);
  if (abs >= 100) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(2);
  if (abs >= 0.01) return v.toFixed(3);
  return v.toPrecision(3);
}

/** Compute "nice" tick step for an axis range so grid values are round numbers. */
function niceStep(range: number, targetTicks: number): number {
  if (!Number.isFinite(range) || range <= 0 || targetTicks <= 0) return 1;
  const rough = range / targetTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const residual = rough / mag;
  let nice: number;
  if (residual <= 1.5) nice = 1;
  else if (residual <= 3) nice = 2;
  else if (residual <= 7) nice = 5;
  else nice = 10;
  return nice * mag;
}

/** Generate tick values from niceStep aligned to nice boundaries. */
function niceTicks(min: number, max: number, targetTicks: number): number[] {
  const step = niceStep(max - min, targetTicks);
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.001; v += step) {
    ticks.push(v);
  }
  return ticks;
}

function buildPath(
  xs: number[],
  ys: Array<number | null>,
  width: number,
  height: number,
  padding: { left: number; right: number; top: number; bottom: number }
): { d: string; minX: number; maxX: number; minY: number; maxY: number; rawMinY: number; rawMaxY: number } {
  const n = Math.min(xs.length, ys.length);
  const pts: Array<{ x: number; y: number; ok: boolean }> = [];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < n; i++) {
    const x = xs[i];
    const y = ys[i];
    if (!Number.isFinite(x) || y == null || !Number.isFinite(y)) {
      pts.push({ x: 0, y: 0, ok: false });
      continue;
    }
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    pts.push({ x, y, ok: true });
  }

  if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
    return { d: '', minX: 0, maxX: 0, minY: 0, maxY: 0, rawMinY: 0, rawMaxY: 0 };
  }

  // Raw min/max from data (used for y-range display).
  const rawMinY = minY;
  const rawMaxY = maxY;

  // But add headroom/footroom for rendering so the plot doesn't touch the frame.
  // 10% of the Y range (or a small fallback if the range is 0).
  let scaleMinY = rawMinY;
  let scaleMaxY = rawMaxY;
  {
    const yRange = rawMaxY - rawMinY;
    const base = Number.isFinite(yRange) && Math.abs(yRange) > 1e-12
      ? Math.abs(yRange)
      : Math.max(1, Math.abs(rawMaxY), Math.abs(rawMinY));
    const pad = base * 0.10;
    scaleMinY = rawMinY - pad;
    scaleMaxY = rawMaxY + pad;
  }

  const innerW = Math.max(1, width - padding.left - padding.right);
  const innerH = Math.max(1, height - padding.top - padding.bottom);

  const dx = Math.max(1e-9, maxX - minX);
  const dy = Math.max(1e-9, scaleMaxY - scaleMinY);

  let d = '';
  let started = false;
  for (const p of pts) {
    if (!p.ok) {
      started = false;
      continue;
    }
    const sx = padding.left + ((p.x - minX) / dx) * innerW;
    const sy = padding.top + (1 - (p.y - scaleMinY) / dy) * innerH;

    if (!started) {
      d += `M ${sx.toFixed(2)} ${sy.toFixed(2)} `;
      started = true;
    } else {
      d += `L ${sx.toFixed(2)} ${sy.toFixed(2)} `;
    }
  }

  // Expose both axis range (minY/maxY) and raw range (rawMinY/rawMaxY).
  return { d: d.trim(), minX, maxX, minY: scaleMinY, maxY: scaleMaxY, rawMinY, rawMaxY };
}

export default function OutcomeArtifactPlotFrame({
  gsUri,
  onClose,
  mode = 'modal',
}: {
  gsUri: string;
  onClose?: () => void;
  mode?: 'modal' | 'inline';
}) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  const query = useQuery({
    queryKey: ['buckets', 'gcsJson', gsUri] as const,
    queryFn: async () => {
      return await bff.get<{ ok: boolean; artifact?: any; error?: string; detail?: string }>('/gcs/json', { gsUri });
    },
    enabled: Boolean(gsUri),
    staleTime: 30_000,
  });

  const plot: ArtifactPlot | null = useMemo(() => {
    const art = (query.data as any)?.artifact;
    if (!art || typeof art !== 'object') return null;
    const p = (art as any).plot;
    if (!p || typeof p !== 'object') return null;
    return p as ArtifactPlot;
  }, [query.data]);

  const x = useMemo(() => {
    const raw = plot?.x_ms;
    if (!Array.isArray(raw)) return [];
    return raw.map(v => (isFiniteNumber(v) ? v : null));
  }, [plot]);

  const seriesKeys = useMemo(() => {
    const s = plot?.series;
    if (!s || typeof s !== 'object') return [];
    return Object.keys(s).sort((a, b) => a.localeCompare(b));
  }, [plot]);

  const [selectedKey, setSelectedKey] = useState<string>('');

  const effectiveKey = useMemo(() => {
    if (selectedKey && seriesKeys.includes(selectedKey)) return selectedKey;
    return seriesKeys[0] || '';
  }, [selectedKey, seriesKeys]);

  const y = useMemo(() => {
    const s = plot?.series;
    if (!s || !effectiveKey) return [] as Array<number | null>;
    const raw = (s as any)[effectiveKey];
    if (!Array.isArray(raw)) return [];
    return raw.map((v: any) => toNumeric(v));
  }, [plot, effectiveKey]);

  const chart = useMemo(() => {
    const xs = x.filter((v): v is number => isFiniteNumber(v));
    const width = 720;
    const height = 260;
    const padding = { left: 58, right: 16, top: 18, bottom: 34 };

    // If x contains nulls (e.g. older padding), fall back to index-based X.
    const xsUse = xs.length === Math.min(x.length, y.length)
      ? (x.map((v, idx) => (isFiniteNumber(v) ? v : idx)) as number[])
      : (Array.from({ length: Math.min(x.length, y.length) }, (_, i) => i));

    const { d, minX, maxX, minY, maxY, rawMinY, rawMaxY } = buildPath(xsUse, y, width, height, padding);

    // Statistics from valid numeric y values.
    const validY = y.filter((v): v is number => v != null && Number.isFinite(v));
    const delta = Number.isFinite(rawMaxY) && Number.isFinite(rawMinY) ? rawMaxY - rawMinY : null;
    const mu = validY.length > 0 ? validY.reduce((sum, v) => sum + v, 0) / validY.length : null;
    const sigma = (() => {
      if (mu == null || validY.length < 2) return null;
      const variance = validY.reduce((s, v) => s + (v - mu) ** 2, 0) / (validY.length - 1);
      return Math.sqrt(variance);
    })();
    const sorted = [...validY].sort((a, b) => a - b);
    const median = sorted.length > 0
      ? (sorted.length % 2 === 1
        ? sorted[Math.floor(sorted.length / 2)]
        : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2)
      : null;

    return {
      d,
      width,
      height,
      padding,
      minX,
      maxX,
      minY,
      maxY,
      rawMinY,
      rawMaxY,
      delta,
      mu,
      sigma,
      median,
      points: Math.min(xsUse.length, y.length),
    };
  }, [x, y]);

  if (!gsUri) return null;

  async function copyToClipboard(text: string) {
    const s = String(text ?? '').trim();
    if (!s) return;

    function bump(next: 'copied' | 'error') {
      setCopyState(next);
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => {
        setCopyState('idle');
        copyTimerRef.current = null;
      }, 1300);
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(s);
        bump('copied');
        return;
      }

      // Fallback for older/locked-down contexts.
      const el = document.createElement('textarea');
      el.value = s;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      bump(ok ? 'copied' : 'error');
    } catch {
      bump('error');
    }
  }

  const copyLabel = copyState === 'copied' ? 'Copied' : (copyState === 'error' ? 'Copy failed' : 'Copy');
  const copyClass =
    'text-xs px-2 py-1 rounded border bg-transparent hover:border-neutral-600 '
    + (copyState === 'copied'
      ? 'text-emerald-300 border-emerald-900/40'
      : (copyState === 'error'
        ? 'text-rose-300 border-rose-900/40'
        : 'text-neutral-400 border-neutral-800'));

  const wrapClassName = mode === 'inline'
    ? 'space-y-2'
    : 'rounded-md border border-neutral-800 backdrop-blur-sm overflow-hidden';

  return (
    <div className={wrapClassName} title={gsUri}>
      {mode === 'modal' && (
        <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
          <div className="text-xs text-neutral-200">Artifact plot</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={copyClass}
              onClick={() => void copyToClipboard(gsUri)}
              aria-label="Copy artifact URI"
            >
              {copyLabel}
            </button>
            {onClose && (
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border text-neutral-400 bg-transparent border-neutral-800 hover:border-neutral-600"
                onClick={onClose}
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      <div className={mode === 'modal' ? 'p-3 space-y-2' : 'space-y-2'}>
        {mode === 'inline' && (
          <div className="flex items-start justify-between gap-2">
            <div className="text-[11px] text-neutral-500 font-mono break-all" title={gsUri}>{gsUri}</div>
            <button
              type="button"
              className={copyClass}
              onClick={() => void copyToClipboard(gsUri)}
              aria-label="Copy artifact URI"
            >
              {copyLabel}
            </button>
          </div>
        )}

        {query.isLoading ? (
          <div className="text-sm text-neutral-400">Loading artifact…</div>
        ) : query.error ? (
          <div className="text-sm text-rose-400">Failed to load artifact: {(query.error as any)?.message ?? String(query.error)}</div>
        ) : !(query.data as any)?.ok ? (
          <div className="text-sm text-rose-400">Failed to load artifact: {(query.data as any)?.error ?? 'unknown_error'}</div>
        ) : !plot || !Array.isArray(plot.x_ms) || !plot.series ? (
          <div className="text-sm text-neutral-500">No plot data found in artifact.</div>
        ) : seriesKeys.length === 0 ? (
          <div className="text-sm text-neutral-500">No plot series found in artifact.</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-neutral-500">Y:</label>
                <select
                  className="text-xs bg-transparent border border-neutral-800 rounded px-2 py-1 text-neutral-200"
                  value={effectiveKey}
                  onChange={(e) => setSelectedKey(e.target.value)}
                >
                  {seriesKeys.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-neutral-600">
                {chart.d ? `${chart.points} pts` : '—'}
              </div>
            </div>

            {chart.d ? (
              <div className="rounded border border-neutral-800/60 overflow-hidden">
                <svg
                  viewBox={`0 0 ${chart.width} ${chart.height}`}
                  preserveAspectRatio="none"
                  className="w-full h-[260px] bg-transparent"
                  role="img"
                  aria-label={`Signal plot – ${effectiveKey}`}
                  style={{ shapeRendering: 'geometricPrecision' }}
                >
                  <defs>
                    {/* Gradient fill under signal curve */}
                    <linearGradient id="signalFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(56,189,248)" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="rgb(56,189,248)" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>

                  {/* ── Grid ─────────────────────────────────────────── */}
                  {(() => {
                    const x0 = chart.padding.left;
                    const x1 = chart.width - chart.padding.right;
                    const y0 = chart.padding.top;
                    const y1 = chart.height - chart.padding.bottom;
                    const innerW = x1 - x0;
                    const innerH = y1 - y0;

                    const minX = chart.minX;
                    const maxX = chart.maxX;
                    const minY = chart.minY;
                    const maxY = chart.maxY;
                    const dx = Math.max(1e-9, maxX - minX);
                    const dy = Math.max(1e-9, maxY - minY);

                    const elements: React.ReactNode[] = [];

                    // ── Nice ticks ──
                    const xTicks = niceTicks(minX, maxX, 12);
                    const yTicks = niceTicks(minY, maxY, 8);

                    // Minor subdivisions: 4 subdivs between each major Y tick.
                    const yMinorStep = yTicks.length >= 2 ? (yTicks[1] - yTicks[0]) / 4 : 0;
                    const xMinorStep = xTicks.length >= 2 ? (xTicks[1] - xTicks[0]) / 4 : 0;

                    // ── Minor X gridlines ──
                    if (xMinorStep > 0) {
                      const start = Math.ceil(minX / xMinorStep) * xMinorStep;
                      for (let v = start; v <= maxX + xMinorStep * 0.001; v += xMinorStep) {
                        const px = x0 + ((v - minX) / dx) * innerW;
                        if (px < x0 - 0.5 || px > x1 + 0.5) continue;
                        elements.push(
                          <line key={`xm-${v}`} x1={px} y1={y0} x2={px} y2={y1}
                            stroke="rgb(64,64,64)" strokeOpacity={0.22} strokeWidth={0.5} />
                        );
                      }
                    }

                    // ── Minor Y gridlines ──
                    if (yMinorStep > 0) {
                      const start = Math.ceil(minY / yMinorStep) * yMinorStep;
                      for (let v = start; v <= maxY + yMinorStep * 0.001; v += yMinorStep) {
                        const py = y0 + (1 - (v - minY) / dy) * innerH;
                        if (py < y0 - 0.5 || py > y1 + 0.5) continue;
                        elements.push(
                          <line key={`ym-${v}`} x1={x0} y1={py} x2={x1} y2={py}
                            stroke="rgb(64,64,64)" strokeOpacity={0.22} strokeWidth={0.5} />
                        );
                      }
                    }

                    // ── Major X gridlines + labels + ticks ──
                    for (const v of xTicks) {
                      const px = x0 + ((v - minX) / dx) * innerW;
                      if (px < x0 - 0.5 || px > x1 + 0.5) continue;
                      elements.push(
                        <line key={`xM-${v}`} x1={px} y1={y0} x2={px} y2={y1}
                          stroke="rgb(82,82,82)" strokeOpacity={0.45} strokeWidth={0.5} />
                      );
                      // Tick mark
                      elements.push(
                        <line key={`xt-${v}`} x1={px} y1={y1} x2={px} y2={y1 + 4}
                          stroke="rgb(115,115,115)" strokeWidth={0.8} />
                      );
                      // Label
                      elements.push(
                        <text key={`xl-${v}`} x={px} y={chart.height - 8}
                          textAnchor="middle" className="fill-neutral-500" fontSize={9}
                          fontFamily="ui-monospace, monospace">
                          {fmtNum(v)}
                        </text>
                      );
                    }

                    // ── Major Y gridlines + labels + ticks ──
                    for (const v of yTicks) {
                      const py = y0 + (1 - (v - minY) / dy) * innerH;
                      if (py < y0 - 0.5 || py > y1 + 0.5) continue;
                      elements.push(
                        <line key={`yM-${v}`} x1={x0} y1={py} x2={x1} y2={py}
                          stroke="rgb(82,82,82)" strokeOpacity={0.45} strokeWidth={0.5} />
                      );
                      // Tick mark
                      elements.push(
                        <line key={`yt-${v}`} x1={x0 - 4} y1={py} x2={x0} y2={py}
                          stroke="rgb(115,115,115)" strokeWidth={0.8} />
                      );
                      // Label
                      elements.push(
                        <text key={`yl-${v}`} x={x0 - 6} y={py + 3}
                          textAnchor="end" className="fill-neutral-500" fontSize={9}
                          fontFamily="ui-monospace, monospace">
                          {fmtNum(v)}
                        </text>
                      );
                    }

                    // ── Border frame ──
                    elements.push(
                      <rect key="frame" x={x0} y={y0} width={innerW} height={innerH}
                        fill="none" stroke="rgb(82,82,82)" strokeOpacity={0.6} strokeWidth={0.8} />
                    );

                    // ── Axis labels ──
                    elements.push(
                      <text key="xAxisLabel" x={x0 + innerW / 2} y={chart.height - 1}
                        textAnchor="middle" className="fill-neutral-600" fontSize={9}
                        fontFamily="ui-monospace, monospace">
                        Time (ms)
                      </text>
                    );
                    elements.push(
                      <text key="yAxisLabel" x={8} y={y0 + innerH / 2}
                        textAnchor="middle" className="fill-neutral-600" fontSize={9}
                        fontFamily="ui-monospace, monospace"
                        transform={`rotate(-90, 8, ${y0 + innerH / 2})`}>
                        {effectiveKey}
                      </text>
                    );

                    return <g>{elements}</g>;
                  })()}

                  {/* ── Mean (μ) reference line ──────────────────────── */}
                  {chart.mu != null && Number.isFinite(chart.mu) && (() => {
                    const x0 = chart.padding.left;
                    const x1 = chart.width - chart.padding.right;
                    const y0 = chart.padding.top;
                    const y1 = chart.height - chart.padding.bottom;
                    const dy = Math.max(1e-9, chart.maxY - chart.minY);
                    const py = y0 + (1 - (chart.mu! - chart.minY) / dy) * (y1 - y0);
                    if (py < y0 || py > y1) return null;
                    return (
                      <g key="mu-line">
                        <line x1={x0} y1={py} x2={x1} y2={py}
                          stroke="rgb(250,204,21)" strokeOpacity={0.55} strokeWidth={0.8}
                          strokeDasharray="6 3" />
                        <text x={x1 + 2} y={py + 3} className="fill-yellow-500/70" fontSize={8}
                          fontFamily="ui-monospace, monospace">
                          μ
                        </text>
                      </g>
                    );
                  })()}

                  {/* ── Signal fill (area under curve) ─────────────── */}
                  {(() => {
                    const y1 = chart.height - chart.padding.bottom;
                    const x0 = chart.padding.left;
                    // Close the path down to the baseline to form a filled area.
                    const parts = chart.d.trim().split(/\s*M\s*/).filter(Boolean);
                    if (parts.length === 0) return null;
                    // Take last point x from the full path for closing.
                    const tokens = chart.d.trim().split(/\s+/);
                    const lastX = parseFloat(tokens[tokens.length - 2]);
                    const firstM = chart.d.trim().match(/^M\s+([\d.]+)/);
                    const firstX = firstM ? parseFloat(firstM[1]) : x0;
                    const areaD = `${chart.d} L ${lastX.toFixed(2)} ${y1.toFixed(2)} L ${firstX.toFixed(2)} ${y1.toFixed(2)} Z`;
                    return <path d={areaD} fill="url(#signalFill)" stroke="none" />;
                  })()}

                  {/* ── Signal line ─────────────────────────────────── */}
                  <path d={chart.d} fill="none"
                    stroke="rgb(56,189,248)" strokeOpacity={0.9} strokeWidth={1.4}
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            ) : (
              <div className="text-sm text-neutral-500">Not enough numeric points to plot.</div>
            )}

            <div className="text-[10px] font-mono text-neutral-500 flex flex-wrap items-center gap-x-5 gap-y-1 pt-1">
              <span className="text-neutral-600">
                x: {Number.isFinite(chart.minX) ? fmtNum(chart.minX) : '—'} → {Number.isFinite(chart.maxX) ? fmtNum(chart.maxX) : '—'} ms
              </span>
              <span className="text-neutral-600">
                y: {Number.isFinite(chart.rawMinY) ? fmtNum(chart.rawMinY) : '—'} → {Number.isFinite(chart.rawMaxY) ? fmtNum(chart.rawMaxY) : '—'}
              </span>
              <span>Δ {chart.delta != null && Number.isFinite(chart.delta) ? fmtNum(chart.delta) : '—'}</span>
              <span className="text-yellow-500/70">μ {chart.mu != null && Number.isFinite(chart.mu) ? fmtNum(chart.mu) : '—'}</span>
              <span>σ {chart.sigma != null && Number.isFinite(chart.sigma) ? fmtNum(chart.sigma) : '—'}</span>
              <span>med {chart.median != null && Number.isFinite(chart.median) ? fmtNum(chart.median) : '—'}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
